import React from 'react'
import { motion } from 'framer-motion'
import {
  UserIcon,
  ClockIcon,
  ChatBubbleLeftRightIcon,
  AdjustmentsHorizontalIcon,
  CheckCircleIcon,
  ExclamationCircleIcon,
} from '@heroicons/react/24/outline'

interface BehaviorSimulationData {
  status: 'active' | 'paused' | 'inactive'
  current_profile: {
    personality_type: string
    engagement_probability: number
    response_delay_range: string
  }
  recent_activity: Array<{
    timestamp: string
    action: string
    delay: number
    humanized: boolean
  }>
  next_actions: Array<{
    scheduled_time: string
    action_type: string
    estimated_delay: number
  }>
  stats: {
    total_responses: number
    avg_delay: number
    humanization_rate: number
  }
}

interface BehaviorSimulationStatusProps {
  data?: BehaviorSimulationData
  onConfigureSettings?: () => void
}

const BehaviorSimulationStatus: React.FC<BehaviorSimulationStatusProps> = ({ data, onConfigureSettings }) => {
  const status = data?.status || 'inactive'
  const profile = data?.current_profile
  const recentActivity = data?.recent_activity || []
  const nextActions = data?.next_actions || []
  const stats = data?.stats

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active':
        return 'text-green-600 bg-green-100'
      case 'paused':
        return 'text-yellow-600 bg-yellow-100'
      default:
        return 'text-gray-600 bg-gray-100'
    }
  }

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'active':
        return <CheckCircleIcon className="w-4 h-4" />
      case 'paused':
        return <ExclamationCircleIcon className="w-4 h-4" />
      default:
        return <ExclamationCircleIcon className="w-4 h-4" />
    }
  }

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-3">
          <UserIcon className="w-6 h-6 text-indigo-600" />
          <h3 className="text-lg font-semibold text-gray-900">Behavior Simulation</h3>
        </div>
        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(status)}`}>
          {getStatusIcon(status)}
          <span className="ml-1 capitalize">{status}</span>
        </span>
      </div>

      {/* Current Profile */}
      {profile && (
        <div className="mb-6 p-4 bg-indigo-50 rounded-lg">
          <h4 className="text-sm font-medium text-indigo-900 mb-2">Current Behavior Profile</h4>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-sm">
            <div>
              <p className="text-indigo-600 font-medium">Personality</p>
              <p className="text-indigo-800 capitalize">{profile.personality_type}</p>
            </div>
            <div>
              <p className="text-indigo-600 font-medium">Engagement Rate</p>
              <p className="text-indigo-800">{((profile.engagement_probability || 0) * 100).toFixed(0)}%</p>
            </div>
            <div>
              <p className="text-indigo-600 font-medium">Response Delay</p>
              <p className="text-indigo-800">{profile.response_delay_range}</p>
            </div>
          </div>
        </div>
      )}

      {/* Stats Grid */}
      {stats && (
        <div className="grid grid-cols-3 gap-4 mb-6">
          <div className="text-center">
            <p className="text-2xl font-semibold text-gray-900">{stats.total_responses || 0}</p>
            <p className="text-sm text-gray-600">Total Responses</p>
          </div>
          <div className="text-center">
            <p className="text-2xl font-semibold text-gray-900">{Math.round((stats.avg_delay || 0) / 60)}m</p>
            <p className="text-sm text-gray-600">Avg Delay</p>
          </div>
          <div className="text-center">
            <p className="text-2xl font-semibold text-gray-900">{((stats.humanization_rate || 0) * 100).toFixed(0)}%</p>
            <p className="text-sm text-gray-600">Humanized</p>
          </div>
        </div>
      )}

      {/* Recent Activity */}
      <div className="mb-6">
        <h4 className="text-sm font-medium text-gray-900 mb-3 flex items-center">
          <ChatBubbleLeftRightIcon className="w-4 h-4 mr-2" />
          Recent Activity
        </h4>
        <div className="space-y-2">
          {recentActivity.length > 0 ? (
            recentActivity.slice(0, 3).map((activity, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, x: -10 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: index * 0.1 }}
                className="flex items-center justify-between p-2 bg-gray-50 rounded text-sm"
              >
                <div className="flex items-center space-x-2">
                  <span className="text-gray-600">{activity.action || 'Unknown action'}</span>
                  {activity.humanized && (
                    <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800">
                      Humanized
                    </span>
                  )}
                </div>
                <span className="text-gray-500">{Math.round((activity.delay || 0) / 60)}m delay</span>
              </motion.div>
            ))
          ) : (
            <p className="text-sm text-gray-500 italic">No recent activity</p>
          )}
        </div>
      </div>

      {/* Next Scheduled Actions */}
      <div>
        <h4 className="text-sm font-medium text-gray-900 mb-3 flex items-center">
          <ClockIcon className="w-4 h-4 mr-2" />
          Upcoming Actions
        </h4>
        <div className="space-y-2">
          {nextActions.length > 0 ? (
            nextActions.slice(0, 3).map((action, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, x: 10 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: index * 0.1 }}
                className="flex items-center justify-between p-2 bg-blue-50 rounded text-sm"
              >
                <div className="flex items-center space-x-2">
                  <span className="text-blue-600 capitalize">{action.action_type || 'unknown'}</span>
                </div>
                <span className="text-blue-500">
                  {action.scheduled_time && !isNaN(new Date(action.scheduled_time).getTime())
                    ? new Date(action.scheduled_time).toLocaleTimeString([], {
                        hour: '2-digit',
                        minute: '2-digit'
                      })
                    : 'TBD'
                  }
                </span>
              </motion.div>
            ))
          ) : (
            <p className="text-sm text-gray-500 italic">No scheduled actions</p>
          )}
        </div>
      </div>

      {/* Settings Link */}
      <div className="mt-6 pt-4 border-t border-gray-200">
        <button
          onClick={onConfigureSettings}
          className="w-full flex items-center justify-center space-x-2 px-3 py-2 text-sm font-medium text-indigo-600 bg-indigo-50 rounded-md hover:bg-indigo-100 transition-colors"
        >
          <AdjustmentsHorizontalIcon className="w-4 h-4" />
          <span>Configure Behavior Settings</span>
        </button>
      </div>
    </div>
  )
}

export default BehaviorSimulationStatus